import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

interface NewsArticle {
  id: string;
  title: string;
  url: string;
  source: string;
  published_at: string;
}

const POLL_INTERVAL = 6 * 60 * 60 * 1000;
const ROTATE_INTERVAL = 5000;
const ANIM_DURATION = 500;

export default function HeroNewsTicker() {
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCrawling, setIsCrawling] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const crawlIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCrawlingRef = useRef(false);
  const indexRef = useRef(0);
  const newsRef = useRef<NewsArticle[]>([]);

  // indexRef와 newsRef를 항상 최신 상태로 유지
  useEffect(() => {
    indexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    newsRef.current = news;
  }, [news]);

  const fetchNews = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('news_articles')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(15);

      if (error) throw error;
      if (data && data.length > 0) {
        setNews(data);
        newsRef.current = data;
        setIsLoading(false);
        return true;
      }
      return false;
    } catch (err) {
      console.error('뉴스 로딩 실패:', err);
      setIsLoading(false);
      return false;
    }
  }, []);

  const triggerCrawl = useCallback(async () => {
    if (isCrawlingRef.current) return;
    isCrawlingRef.current = true;
    setIsCrawling(true);
    setIsLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/news-crawler`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`크롤링 실패: ${response.status} - ${errorText}`);
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await fetchNews();
    } catch (err) {
      console.error('크롤링 트리거 실패:', err);
    } finally {
      isCrawlingRef.current = false;
      setIsCrawling(false);
      setIsLoading(false);
    }
  }, [fetchNews]);

  // 초기 데이터 로드
  useEffect(() => {
    const init = async () => {
      const hasData = await fetchNews();
      if (!hasData) await triggerCrawl();
    };
    init();
    crawlIntervalRef.current = setInterval(() => triggerCrawl(), POLL_INTERVAL);
    return () => {
      if (crawlIntervalRef.current) clearInterval(crawlIntervalRef.current);
    };
  }, [fetchNews, triggerCrawl]);

  // 로테이션: 마운트 후 딱 한 번만 interval 등록, ref로 최신 값 참조
  useEffect(() => {
    // 이미 interval이 있으면 중복 등록 방지
    if (intervalRef.current) return;

    intervalRef.current = setInterval(() => {
      const len = newsRef.current.length;
      if (len <= 1) return;

      // 애니메이션 시작
      setIsAnimating(true);

      setTimeout(() => {
        setCurrentIndex((prev) => {
          const next = (prev + 1) % len;
          indexRef.current = next;
          return next;
        });
        setIsAnimating(false);
      }, ANIM_DURATION);
    }, ROTATE_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  // 마운트 시 딱 한 번만 실행
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTimeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;
    return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    margin: '0 auto',
    marginBottom: '1.75rem',
  };

  const tickerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    background: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '9999px',
    padding: '0.5rem 1rem',
    width: '100%',
    maxWidth: '36rem',
    margin: '0 auto',
    overflow: 'hidden',
  };

  if (isLoading || isCrawling) {
    return (
      <div style={containerStyle}>
        <div style={tickerStyle}>
          <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <span style={{ position: 'relative', display: 'inline-flex', width: '0.5rem', height: '0.5rem' }}>
              <span className="animate-ping" style={{ position: 'absolute', inset: 0, borderRadius: '9999px', background: '#60a5fa', opacity: 0.75 }}></span>
              <span style={{ position: 'relative', borderRadius: '9999px', width: '0.5rem', height: '0.5rem', background: '#3b82f6' }}></span>
            </span>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
              {isCrawling ? '수집중' : '로딩'}
            </span>
          </span>
          <span style={{ flexShrink: 0, width: '1px', height: '1rem', background: 'rgba(255,255,255,0.2)' }}></span>
          <span style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
            {isCrawling ? '최신 뉴스를 수집하고 있습니다...' : '뉴스를 불러오는 중...'}
          </span>
        </div>
      </div>
    );
  }

  if (news.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={tickerStyle}>
          <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <span style={{ borderRadius: '9999px', width: '0.5rem', height: '0.5rem', background: '#9ca3af', display: 'inline-block' }}></span>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>대기중</span>
          </span>
          <span style={{ flexShrink: 0, width: '1px', height: '1rem', background: 'rgba(255,255,255,0.2)' }}></span>
          <span style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>뉴스 수집을 준비하고 있습니다</span>
        </div>
      </div>
    );
  }

  const currentArticle = news[currentIndex];
  const nextArticle = news[(currentIndex + 1) % news.length];

  return (
    <div style={containerStyle}>
      <div style={{ ...tickerStyle, cursor: 'pointer' }}>
        {/* 속보 뱃지 */}
        <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <span style={{ position: 'relative', display: 'inline-flex', width: '0.5rem', height: '0.5rem' }}>
            <span className="animate-ping" style={{ position: 'absolute', inset: 0, borderRadius: '9999px', background: '#f87171', opacity: 0.75 }}></span>
            <span style={{ position: 'relative', borderRadius: '9999px', width: '0.5rem', height: '0.5rem', background: '#ef4444' }}></span>
          </span>
          <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#f87171', textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
            속보
          </span>
        </span>

        {/* 구분선 */}
        <span style={{ flexShrink: 0, width: '1px', height: '1rem', background: 'rgba(255,255,255,0.2)' }}></span>

        {/* 뉴스 텍스트 슬라이드 영역 */}
        <div style={{ position: 'relative', height: '1.5rem', overflow: 'hidden', flex: 1, minWidth: 0 }}>
          {/* 현재 기사 */}
          <a
            href={currentArticle.url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              transition: `transform ${ANIM_DURATION}ms ease-in-out, opacity ${ANIM_DURATION}ms ease-in-out`,
              transform: isAnimating ? 'translateY(-100%)' : 'translateY(0)',
              opacity: isAnimating ? 0 : 1,
              textDecoration: 'none',
            }}
          >
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentArticle.title}
            </span>
          </a>
          {/* 다음 기사 */}
          <a
            href={nextArticle.url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              transition: `transform ${ANIM_DURATION}ms ease-in-out, opacity ${ANIM_DURATION}ms ease-in-out`,
              transform: isAnimating ? 'translateY(0)' : 'translateY(100%)',
              opacity: isAnimating ? 1 : 0,
              textDecoration: 'none',
            }}
          >
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {nextArticle.title}
            </span>
          </a>
        </div>

        {/* 시간 표시 */}
        <span style={{ flexShrink: 0, fontSize: '0.625rem', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
          {getTimeAgo(currentArticle.published_at)}
        </span>
      </div>
    </div>
  );
}
